import { useEffect, useRef, useState, useCallback } from "react";

const TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes
const WARNING_MS = 60 * 1000; // Show warning 1 minute before

export function useSessionTimeout(onLogout, enabled = true) {
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const timerRef = useRef(null);
  const warningRef = useRef(null);
  const countdownRef = useRef(null);

  const resetTimer = useCallback(() => {
    if (!enabled) return;
    setShowWarning(false);
    clearTimeout(timerRef.current);
    clearTimeout(warningRef.current);
    clearInterval(countdownRef.current);

    warningRef.current = setTimeout(() => {
      setShowWarning(true);
      setCountdown(60);
      countdownRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, TIMEOUT_MS - WARNING_MS);

    timerRef.current = setTimeout(() => {
      setShowWarning(false);
      clearInterval(countdownRef.current);
      onLogout();
    }, TIMEOUT_MS);
  }, [onLogout, enabled]);

  const extendSession = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  useEffect(() => {
    if (!enabled) return;
    const events = ["mousedown", "keydown", "scroll", "touchstart", "mousemove"];
    const handler = () => resetTimer();
    events.forEach(e => window.addEventListener(e, handler, { passive: true }));
    resetTimer();
    return () => {
      events.forEach(e => window.removeEventListener(e, handler));
      clearTimeout(timerRef.current);
      clearTimeout(warningRef.current);
      clearInterval(countdownRef.current);
    };
  }, [resetTimer, enabled]);

  return { showWarning, countdown, extendSession };
}

export function SessionTimeoutWarning({ countdown, onExtend }) {
  return (
    <div className="fixed inset-0 z-[200] bg-black/60 flex items-center justify-center" data-testid="session-timeout-warning">
      <div className="bg-white p-8 max-w-md w-full mx-4 border-t-4 border-[#DC2626]">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-[#FEF2F2] flex items-center justify-center">
            <span className="text-3xl font-mono font-black text-[#DC2626]">{countdown}</span>
          </div>
          <h2 className="font-heading font-bold text-xl text-[#0F172A] mb-2">Session Expiring</h2>
          <p className="text-[#64748B] text-sm mb-6">
            Your session will expire in {countdown} second{countdown !== 1 ? "s" : ""} due to inactivity.
            Click below to continue working.
          </p>
          <button
            onClick={onExtend}
            className="w-full py-3 bg-[#2563EB] hover:bg-[#0F172A] text-white font-bold tracking-wide transition-colors"
            data-testid="extend-session-btn"
          >
            CONTINUE SESSION
          </button>
          <p className="text-xs text-[#94A3B8] mt-3">NIST 800-171 · 15-minute idle timeout</p>
        </div>
      </div>
    </div>
  );
}
